import { ResponseError } from '@backstage/errors';
import { stringifyEntityRef } from '@backstage/catalog-model';
import qs from 'qs';
import stableStringify from 'fast-json-stable-stringify';

class TechInsightsClient {
  discoveryApi;
  identityApi;
  apiCache = /* @__PURE__ */ new Map();
  constructor(options) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }
  /**
   * Get facts for a specific entity
   * @param entity - a component reference
   * @param facts - a list fact ids to fetch
   * @public
   */
  async getFacts(entity, facts) {
    const query = qs.stringify({
      entity: stringifyEntityRef(entity),
      ids: facts
    });
    return await this.api(`/facts/latest?${query}`);
  }
  /**
   * Get all checks.
   * This will not return the actual status, but only metadata. To get the status use the /run endpoint
   * @public
   */
  async getAllChecks() {
    return this.api("/checks");
  }
  /**
   * Get schemas of facts.
   * Use this for example to understand what the return values will be
   * @public
   */
  async getFactSchemas() {
    return this.api("/fact-schemas");
  }
  /**
   * Run checks for a specific entity
   * @param entityParams - reference to an entity
   * @param checks - IDs of checks to run
   * @public
   */
  async runChecks(entityParams, checks) {
    const { namespace, kind, name } = entityParams;
    const requestBody = { checks };
    return this.api(
      `/checks/run/${encodeURIComponent(namespace)}/${encodeURIComponent(
        kind
      )}/${encodeURIComponent(name)}`,
      {
        method: "POST",
        body: JSON.stringify(requestBody)
      }
    );
  }
  /**
   * Return checks for multiple entities
   * @param entities - list of entity references
   * @param checks - list of check IDs
   * @public
   */
  async runBulkChecks(entities, checks) {
    const checkIds = checks ? checks.map((check) => check.id) : [];
    const requestBody = {
      entities,
      checks: checkIds.length > 0 ? checkIds : void 0
    };
    return this.api("/checks/run", {
      method: "POST",
      body: JSON.stringify(requestBody)
    });
  }
  getCacheKey(path, init) {
    return `${path} ${stableStringify(init ?? {})}`;
  }
  async api(path, init) {
    const url = await this.discoveryApi.getBaseUrl("tech-insights");
    const cacheKey = this.getCacheKey(`${url}${path}`, init);
    const cached = this.apiCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const result = (async () => {
      const token = await this.getToken();
      const headers = new Headers(init?.headers);
      if (!headers.has("content-type"))
        headers.set("content-type", "application/json");
      if (token && !headers.has("authorization")) {
        headers.set("authorization", `Bearer ${token}`);
      }
      const request = new Request(`${url}${path}`, {
        ...init,
        headers
      });
      return fetch(request).then(async (response) => {
        if (!response.ok) {
          throw await ResponseError.fromResponse(response);
        }
        return response.json();
      });
    })();
    this.apiCache.set(cacheKey, result);
    setTimeout(() => {
      this.apiCache.delete(cacheKey);
    }, 2e3);
    return result;
  }
  async getToken() {
    let result;
    if ("getCredentials" in this.identityApi) {
      result = await this.identityApi.getCredentials();
    } else {
      result = await this.identityApi.getPluginRequestToken({
        onBehalfOf: await this.identityApi.getOwnServiceCredentials(),
        targetPluginId: "tech-insights"
      });
    }
    return result.token ?? null;
  }
}

export { TechInsightsClient };
//# sourceMappingURL=TechInsightsClient.esm.js.map
